import React from 'react';
import Spinner from './Spinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
}

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: `
    bg-accent text-accent-contrast shadow-sm
    hover:brightness-[1.08]
    focus:ring-accent
    disabled:opacity-50
  `,
  secondary: `
    bg-surface-3 text-text-secondary border border-border-1
    hover:brightness-[1.08] hover:border-border-2
    focus:ring-accent
    disabled:opacity-50
  `,
  ghost: `
    bg-transparent text-accent-emphasis
    hover:bg-accent-soft
    focus:ring-accent
    disabled:opacity-50
  `,
  danger: `
    bg-red-500/12 text-red-400 border border-red-500/20
    hover:bg-red-500/20
    focus:ring-red-500
    disabled:opacity-50
  `,
  success: `
    bg-green-600 text-white shadow-sm
    hover:brightness-[1.08]
    focus:ring-green-500
    disabled:opacity-50
  `,
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-[11.5px]',
  md: 'px-4 py-2 text-[12.5px]',
  lg: 'px-6 py-2.5 text-[13px]',
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  className = '',
  type = 'button',
  ...props
}) => {
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center
        font-semibold rounded-button
        transition-all duration-150
        focus:outline-none focus:ring-2 focus:ring-offset-0
        disabled:cursor-not-allowed
        active:scale-[0.98]
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {loading && (
        <Spinner
          size="sm"
          color={variant === 'secondary' || variant === 'ghost' ? 'accent' : 'white'}
          className="mr-2"
          aria-label="Loading"
        />
      )}
      {children}
    </button>
  );
};

export default Button;
